import type { PlatformSession } from "../api/types";

/**
 * Thin typed `localStorage` wrapper for the persisted operator session — same pattern as
 * `dashboard-admin/src/lib/storage.ts`'s `AuthSession`, so a page reload doesn't kick the
 * operator back to the login screen. Read/written by BOTH `PlatformAuthContext` (for UI
 * state) and `realPlatformApiClient.ts` (for its own internal token-refresh bookkeeping —
 * see that file's top doc comment for why it needs to independently read/refresh from this
 * same key rather than receiving a token per call). With `mockPlatformApiClient`,
 * `access_token` is an opaque random string with no cryptographic guarantee; with
 * `realPlatformApiClient` (the default), it's a real signed `aud:"platform"` JWT — a plain,
 * non-`httpOnly` `localStorage` session is a documented simplification appropriate for this
 * internal operator tool, the same tradeoff `dashboard-admin`/`ecommerce-store` already make,
 * not production-grade session-token hardening (no XSS-proof storage).
 */

const PLATFORM_SESSION_KEY = "sl-platform-admin-session";

export function loadPlatformSession(): PlatformSession | null {
  const raw = localStorage.getItem(PLATFORM_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PlatformSession;
    if (new Date(parsed.expires_at).getTime() < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePlatformSession(session: PlatformSession): void {
  localStorage.setItem(PLATFORM_SESSION_KEY, JSON.stringify(session));
}

export function clearPlatformSession(): void {
  localStorage.removeItem(PLATFORM_SESSION_KEY);
}
